import { Injectable } from '@angular/core';
import { DatePipe } from '@angular/common';
import {
  FormBase,
  TextBox,
  CheckBox,
  Select,
  AcmicService,
} from '@mapfre-design-library/src/public_api';
import { CompanyTypeService } from '../../_iam/company-type.service';
import { Constants } from '@models/constants';
import { PolicyDetailsService } from '@services/my-insurance/policy-details.service';

@Injectable({
  providedIn: 'root',
})
export class EnrollEftEpayService {
  constructor(
    private datePipe: DatePipe,
    private acmicService: AcmicService,
    private companyTypeService: CompanyTypeService,
    private policyDetailService: PolicyDetailsService
  ) {}

  getInputs(policyNumber?: string) {
    let deductionOption = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28]
    let company = "MAPFRE";
    if(policyNumber != null){
      company = this.companyTypeService.getCompany(policyNumber);
        if(policyNumber.startsWith("NH")) {
          deductionOption = [1, 15, 28]
        }
    }

    let inputs: FormBase<any>[] = [];

    if(company === "TW" || company === "ACIC"){
      inputs = [
        this.getAccountNameField('enrollInEft_accountName', "Bank account holder’s name", 'VALID_NAME_VALIDATION_MESSAGE'),
        this.getAccountNameField('enrollInEft_bankName', 'BANK_NAME', 'VALID_NAME_VALIDATION_MESSAGE'),
        this.getRoutingNumberField('enrollInEft_routingNumber', 'ROUTING_ABA_NUMBER', 'VALID_ROUTING_NUMBER'),
        this.getAccountNumberField('enrollInEft_accountNumber', 'BANK_ACCOUNT_NUMBER', 'VALID_ACCOUNT_NUMBER'),
        this.getConfirmAccountNumberField('enrollInEft_confirmAccountNumber', 'CONFIRM_BANK_ACCOUNT_NUMBER', 'VALID_ACCOUNT_NUMBER'),
        this.getCheckBox()
    ]
    }else{
      inputs = [
        this.getAccountNameField('enrollInEft_accountName', 'BANK_ACCOUNT_HOLDERS_NAME', 'VALID_NAME_VALIDATION_MESSAGE'),
        this.getRoutingNumberField('enrollInEft_routingNumber', 'ROUTING_ABA_NUMBER', 'VALID_ROUTING_NUMBER'),
        this.getAccountNumberField('enrollInEft_accountNumber', 'BANK_ACCOUNT_NUMBER', 'VALID_ACCOUNT_NUMBER'),
        this.getConfirmAccountNumberField('enrollInEft_confirmAccountNumber', 'CONFIRM_BANK_ACCOUNT_NUMBER', 'VALID_ACCOUNT_NUMBER'),
        this.getCheckBox()
      ]
    }

    if(company !== "TW") {
      inputs.unshift(this.getDeductionDateSelect(deductionOption))
    }

    return inputs;

  }

  getCheckBox(){
    return new CheckBox({
      additionalClasses: Constants.FORM_CONTROL,
      key: 'enrollInEft_legal',
      inputType: 'checkbox',
      label: 'TERMS_AND_CONDITIONS',
      required: true,
      type: 'checkbox',
    })
  }

  getDeductionDateSelect(option: number[]){
    return new Select({
      additionalClasses: Constants.FORM_CONTROL,
      inputType: 'select',
      key: 'enrollInEft_deductionDate',
      label: this.acmicService.isACMIC() ? 'MONTHLY_DEDUCTION_DATE' : 'Select',
      required: true,
      options: option,
      type: 'select',
      validationMessageError: 'REQUIRED',
    })
  }

  getAccountNameField(key?: string, label?: string, msg?: string,placeholder?:string) {
    const accountName = new TextBox({
      additionalClasses: Constants.FORM_CONTROL,
      inputType: 'text',
      key,
      label,
      required: true,
      type: 'text',
      validationMessageError: msg,
      placeholder:this.acmicService.isACMIC() ? placeholder : '',
    }, );
    return accountName;
  }

  getRoutingNumberField(key?: string, label?: string, msg?: string, placeholder?:string) {
    const routingNumber = new TextBox({
      additionalClasses: Constants.FORM_CONTROL,
      inputType: 'bank',
      key,
      label,
      required: true,
      maxLength: 9,
      minLength: 9,
      type: 'tel',
      validationMessageError: msg,
      placeholder:this.acmicService.isACMIC() ? placeholder : '',
      showWarnIcon: true
    }); 
    return routingNumber;
  }

  getAccountNumberField(key?: string, label?: string, msg?: string, placeholder?:string) {
    const accountNumber = new TextBox({
      additionalClasses: Constants.FORM_CONTROL,
      inputType: 'bank_account_number',
      key,
      label,
      required: true,
      maxLength: 17,
      minLength: 5,
      type: 'tel',
      validationMessageError: msg,
      matchMessageError: "Account number and confirmation are not the same.",
      placeholder:this.acmicService.isACMIC() ? placeholder : '',
      showWarnIcon: true
    });
    return accountNumber;
  }

  getConfirmAccountNumberField(key?: string, label?: string, msg?: string, placeholder?:string) {
    const confirmAccountNumber = new TextBox({
      additionalClasses: Constants.FORM_CONTROL,
      inputType: 'bank_account_number',
      key,
      label,
      required: true,
      type: 'tel',
      maxLength: 17,
      minLength: 5,
      validationMessageError: msg,
      matchMessageError: "Account number and confirmation are not the same.",
      placeholder:this.acmicService.isACMIC() ? placeholder : '',
      showWarnIcon: true
    });
    return confirmAccountNumber;
  }

  setPrevEftRoute(url) {
    localStorage.setItem('prevEftRoute', url);
  }

  getPrevEftRoute() {
    return localStorage.getItem('prevEftRoute');
  }

  getImportantEditNotice(changedObj: any): any{
    const accNum = changedObj.accountNum;
    const currAccNum = changedObj.currAccountNum;
    const isBankAccountChanged = currAccNum !== accNum;
    const maskedAccountNum = accNum?.slice(-4).padStart(accNum.length, '*');

    const deductionDay = String(changedObj.deductionDay).padStart(2, '0');
    const currDeductionDay = changedObj.currDeductionDay;

    const isDeductionDayChanged = +deductionDay !== +currDeductionDay;

    if(!isBankAccountChanged && !isDeductionDayChanged)
      return null;

    const policyNum = changedObj.policy?.policynumber?.policyNumber;
    let billedDate = { withdrawalDateCount: 3, displayedDate: "", hasBilledInvoice: false };

    if (changedObj.policy && changedObj.policy.scheduleBills) {
      billedDate = this.calculateBilledDate(changedObj.policy.scheduleBills)
    }

    let editImpNotice = '';
    let impNotice = '';

    if(isBankAccountChanged && isDeductionDayChanged){
      editImpNotice = `You are changing your deduction date to the <b>${deductionDay}</b> of each month and your bank account 
                    to <b>${maskedAccountNum}</b>. These changes will apply to this payment and all future invoices.`;
      impNotice = `Your deduction date have been changed to the <b>${deductionDay}</b> of each month and your bank account to <b>${maskedAccountNum}</b>. 
                    These changes will apply to this payment and all future invoices.`

      if(billedDate.hasBilledInvoice){
        editImpNotice = `You are changing your deduction date to the <b>${deductionDay}</b> of each month and your bank account 
                      to <b>${maskedAccountNum}</b>. These changes will apply beginning with your next invoice.`;
        impNotice = `Your deduction date has been changed to the <b>${deductionDay}</b> of each month and your bank account 
                      to <b>${maskedAccountNum}</b>. These changes will apply beginning with your next invoice on <b>${billedDate.displayedDate}</b>.`
      }
      if(policyNum?.length === 6){
        editImpNotice = `You are changing your deduction date to the <b>${deductionDay}</b> of each month 
                            and your bank account to <b>${maskedAccountNum}</b>.`;
        impNotice = `Your deduction date have been changed to the <b>${deductionDay}</b> of each month 
                      and your bank account to <b>${maskedAccountNum}</b>.`
      }

      return {editImpNotice: editImpNotice, impNotice: impNotice};
    }
    if(isDeductionDayChanged){
      editImpNotice = `You are changing your deduction date from the <b>${currDeductionDay}</b> to the <b>${deductionDay}</b> of each month. 
                    This payment and all future invoices will be deducted on this date.`;
      impNotice = `Your deduction date has been changed from the <b>${currDeductionDay}</b> to the <b>${deductionDay}</b> of each month. 
                    This payment and all future invoices will be deducted on this date.`

      if(billedDate.hasBilledInvoice){
        editImpNotice = `You are changing your deduction date from the <b>${currDeductionDay}</b> to the <b>${deductionDay}</b> of each month.
                        This change will apply beginning with your next invoice.`;
        impNotice = `Your deduction date has been changed to <b>${deductionDay}</b>. 
                      This change will apply beginning with your next invoice on <b>${billedDate.displayedDate}</b>.`
      }

      if(policyNum?.length === 6){
        editImpNotice = `You are changing your deduction date from the <b>${currDeductionDay}</b> to the <b>${deductionDay}</b> of each month.`;
        impNotice = `Your deduction date has been changed from the <b>${currDeductionDay}</b> to the <b>${deductionDay}</b> of each month.`
      }

      return {editImpNotice: editImpNotice, impNotice: impNotice};
    }

    editImpNotice = `You are changing your bank account to <b>${maskedAccountNum}</b>. 
                  This payment and all future invoices will be withdrawn from this account.`;
    impNotice = `Your bank account has been changed to <b>${maskedAccountNum}</b>. 
                  This payment and all future invoices will be withdrawn from this account.`

    if(billedDate.hasBilledInvoice){
      editImpNotice = `You are changing your bank account to <b>${maskedAccountNum}</b>. 
                    This change will apply beginning with your next invoice on <b>${billedDate.displayedDate}</b>.`;
      impNotice = `Your bank account has been changed to <b>${maskedAccountNum}</b>. 
                    This change will apply beginning with your next invoice on <b>${billedDate.displayedDate}</b>.`              
    }

    if(policyNum?.length === 6){
      editImpNotice = `You are changing your bank account to <b>${maskedAccountNum}</b>.`;
      impNotice = `Your bank account has been changed to <b>${maskedAccountNum}</b>.`
    }

    return {editImpNotice: editImpNotice, impNotice: impNotice};
  }

  private calculateBilledDate(scheduleBills: any[]): any{
    const billedDate = {
      withdrawalDateCount: 3,      
      displayedDate: '',
      hasBilledInvoice: false
    };
    let billedIndex = -2;

    const sortedScheduleBills = [...scheduleBills].sort((a, b) => {
        return new Date(a.dateDue).getTime() - new Date(b.dateDue).getTime();
      })

      sortedScheduleBills.forEach((scheduledBill, index) => {
        if (scheduledBill.invoiceState === 'Billed') {
          billedDate.withdrawalDateCount = this.policyDetailService.getDayDifference(scheduledBill.dateDue);
          billedDate.hasBilledInvoice = true;
          billedIndex = index;
        }
        if (index === billedIndex + 1) {
          billedDate.displayedDate = this.datePipe?.transform(
              scheduledBill.dateDue,
              Constants.DATE_MM_DD_YYYY
            );
        }
      });
    
    return billedDate;
  }
}
